import type { BorderEdge, RegionBorders } from '@tsagko/label-contracts'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

// Module scope can't call t(): carry the KEY and translate at render.
// `borders.*` is deliberately neutral — this picker is shared by the block
// configurator and the template designer.
const BORDER_EDGES: { key: keyof RegionBorders; labelKey: string }[] = [
  { key: 'top', labelKey: 'borders.edge.top' },
  { key: 'bottom', labelKey: 'borders.edge.bottom' },
  { key: 'left', labelKey: 'borders.edge.left' },
  { key: 'right', labelKey: 'borders.edge.right' },
]
const DEFAULT_PEN: BorderEdge = { thicknessMm: 0.4, style: 'solid' }

export function BorderPicker({
  borders,
  onSetEdge,
}: {
  borders?: RegionBorders
  onSetEdge: (edge: keyof RegionBorders, value: BorderEdge | undefined) => void
}) {
  const { t } = useTranslation()
  const [focus, setFocus] = useState<keyof RegionBorders | undefined>(undefined)
  const focused = focus ? borders?.[focus] : undefined
  return (
    <section className="mb-4" data-testid="rpp-borders">
      <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
        {t('borders.title')}
      </label>
      <div className="mt-2 grid grid-cols-2 gap-1">
        {BORDER_EDGES.map(e => {
          const on = Boolean(borders?.[e.key])
          return (
            <button
              key={e.key}
              type="button"
              data-testid={`rpp-border-${e.key}`}
              aria-pressed={on}
              onClick={() => {
                if (on) {
                  onSetEdge(e.key, undefined)
                  if (focus === e.key) setFocus(undefined)
                } else {
                  onSetEdge(e.key, DEFAULT_PEN)
                  setFocus(e.key)
                }
              }}
              className={`rounded border px-2 py-1 text-xs ${
                on
                  ? 'border-sky-500 bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300'
                  : 'border-stone-300 text-stone-600 dark:border-stone-700 dark:text-stone-400'
              }`}
            >
              {t(e.labelKey)} {on ? '▣' : '▢'}
            </button>
          )
        })}
      </div>
      {focus && focused && (
        <div
          data-testid="rpp-border-editor"
          className="mt-2 rounded border border-stone-200 p-2 dark:border-stone-700"
        >
          <p className="text-[11px] text-stone-500">
            {t('borders.editing', { edge: t(`borders.edge.${focus}`) })}
          </p>
          <label className="mt-1 block text-xs">
            <span className="text-stone-500">{t('borders.thickness')}</span>
            <input
              data-testid="rpp-border-thickness"
              type="number"
              step="0.1"
              min="0.1"
              value={focused.thicknessMm}
              onChange={ev => {
                const v = Number(ev.target.value)
                if (!Number.isFinite(v) || v <= 0) return
                onSetEdge(focus, { ...focused, thicknessMm: v })
              }}
              className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
            />
          </label>
          <label className="mt-2 block text-xs">
            <span className="text-stone-500">{t('borders.style')}</span>
            <select
              data-testid="rpp-border-style"
              value={focused.style}
              onChange={ev => onSetEdge(focus, { ...focused, style: ev.target.value as BorderEdge['style'] })}
              className="mt-1 w-full rounded border border-stone-300 bg-white px-2 py-1 dark:border-stone-700 dark:bg-stone-950"
            >
              <option value="solid">{t('borders.styleSolid')}</option>
              <option value="dashed">{t('borders.styleDashed')}</option>
              <option value="dotted">{t('borders.styleDotted')}</option>
            </select>
          </label>
        </div>
      )}
    </section>
  )
}
